<template>
    <div>
        <hero-banner :title="heroTitle" :show-icon="currentStepNumber === 5" />
        <section class="section pt-3">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-lg-9 col-md-10 col-sm-12">
                        <div class="pt-5">
                            <div id="loader" v-if="isLoading">
                                <div class="spinner"></div>
                            </div>

                            <h3 v-if="!sessions">There are no info sessions currently available</h3>

                            <form ref="form" v-if="sessions">
                                <current-selected-session
                                    v-if="currentStepNumber != 1"
                                    :form-data="formData"
                                ></current-selected-session>
                                <div
                                    v-if="currentStepNumber <= totalSteps"
                                    class="progress-bar-container"
                                >
                                    <ProgressBar
                                        :active-step="currentStepNumber"
                                        :total-steps="totalSteps"
                                    ></ProgressBar>
                                </div>

                                <select-info-session
                                    :sessions="props.sessions"
                                    v-if="currentStepNumber === 1"
                                    :step="currentStepNumber"
                                    @submit-button="updateCounterAndFormData"
                                    @setStep="redirectToEndStep"
                                    :form-data="formData"
                                    :group-session-id="groupSessionId"
                                    :programme="programme"
                                    :content="content"
                                ></select-info-session>
                                <personal-details
                                    v-if="currentStepNumber === 2"
                                    @submit-button="updateCounterAndFormData"
                                    @goBackAStep="updateCounterGoBack"
                                    :form-data="formData"
                                ></personal-details>
                                <address-details
                                    v-if="currentStepNumber === 3"
                                    @submit-button="updateCounterAndFormData"
                                    @goBackAStep="updateCounterGoBack"
                                    :form-data="formData"
                                ></address-details>
                                <terms-and-conditions
                                    v-if="currentStepNumber === 4"
                                    @submit-button="updateCounterAndFormData"
                                    @goBackAStep="updateCounterGoBack"
                                    :form-data="formData"
                                ></terms-and-conditions>
                                <booking-confirmation
                                    v-if="currentStepNumber === 5"
                                    :selected="formData.info_session_id"
                                    :sessions="sessions"
                                />
                                <div v-if="supportMessage !== null">
                                    <h3>{{ supportMessage.title }}</h3>
                                    <div v-html="supportMessage.items[0]" />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import ProgressBar from "../../../ProgressBar";
import Form from "../../../../Form.js";
import CurrentSelectedSession from "./CurrentSelectedSession";
import SelectInfoSession from "./SelectInfoSession";
import PersonalDetails from "./PersonalDetails";
import AddressDetails from "./AddressDetails";
import TermsAndConditions from "./TermsAndConditions";
import BookingConfirmation from "./BookingConfirmation";
import HeroBanner from "../HeroBanner";
import { locationFormatter } from "./locationFormatter.js";
import { computed, nextTick, ref, useTemplateRef } from "vue";

const props = defineProps({
    sessions: Array,
    previousSession: Object,
    groupSessionId: String,
    programme: String,
    supportMessage: Object,
    content: Object
})

let currentStepNumber = ref(1)
const previousStepNumber = ref(0)
const isLoading = ref(false)
const totalSteps = 4
const form = useTemplateRef('form')

let formData = ref(new Form({
    email_address: null,
    info_session_id: null,
    pronouns: null,
    first_name: null,
    last_name: null,
    dob: null,
    phone_number: null,
    addressLine1: null,
    addressLine2: null,
    addressLine3: null,
    locality: null,
    district: null,
    ward: null,
    post_code: null,
    password: null,
    password_confirmation: null,
    code_of_conduct: null,
    terms_and_conditions: null,
    privacy_policy_consent: null,
    contact_by_text: null,
    contact_by_phone: null,
    contact_by_email: null,
    contact_by_post: null,
    start_date: null,
    start_time: null,
    end_time: null,
    _method: "POST"       
}, props.previousSession))

let heroTitle = computed(() => {
    return currentStepNumber.value === 5
                ? "Booking confirmed"
                : "Book your " + locationFormatter(props.groupSessionId) + " " + locationFormatter(props.programme) + " Information session"
})

nextTick(() => {
    window.dataLayer = window.dataLayer || [];

    if(currentStepNumber.value !== 5) {
            if(previousStepNumber.value < currentStepNumber.value )
                window.dataLayer.push({'event': 'getInto_registration_step_' + previousStepNumber.value  + '_submitted'});
            else
                window.dataLayer.push({'event': 'getInto_registration_step_' + currentStepNumber.value + '_returned'})
        } else {
            window.dataLayer.push({'event': 'getInto_registration_form_completed'});
        }
})

function updateCounterAndFormData({ data }) {
    currentStepNumber.value += 1;
    previousStepNumber.value = currentStepNumber.value - 1;
    formData.value = {
        ...formData.value,
        ...data
    };
    scrollToBeginning();
}

function redirectToEndStep({ data }) {
    currentStepNumber.value += 4;
    scrollToBeginning();
    formData.value = {
        ...formData.value,
        ...data
    };
}

function updateCounterGoBack() {
    currentStepNumber.value -= 1;
    previousStepNumber.value = currentStepNumber.value;
    scrollToBeginning();
}

function scrollToBeginning() {
    if (form.value && currentStepNumber.value < 5 ) {
        const { top } = form.value.getBoundingClientRect();
        const offsetY = Math.max(0, window.pageYOffset + top - 30);
        window.scroll({ top: offsetY, left: 0, behavior: "smooth" });
    } else {
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
    }
}
</script>
